import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';
import { Option } from '@/components/inputs/AutocompleteInput';
import { useGetDepartmentsQuery } from '../services/Addresses';

interface UseDestinationStateOrDepartmentsAutocompleteProps {
  search?: string;
  minLength?: number;
}

const useDestinationStateOrDepartmentsAutocomplete = ({
  search,
  minLength = 3,
}: UseDestinationStateOrDepartmentsAutocompleteProps = {}) => {
  const { debouncedSearch, shouldSearch, isDebouncePending } =
    useAutocompleteSearch(search, minLength);

  const {
    data: departmentsData,
    isFetching,
    error,
  } = useGetDepartmentsQuery(
    {
      paginated: { page: 1, pageSize: 50 },
      search: shouldSearch ? debouncedSearch : undefined,
    },
    { skip: !shouldSearch }
  );

  const resolvedOptions: Option[] = useMemo(() => {
    if (!departmentsData?.items?.length) return [];

    return departmentsData.items.map((dept) => ({
      label: dept.name,
      data: dept,
    }));
  }, [departmentsData?.items]);

  const options = useStableOptions(resolvedOptions, {
    isPending: isFetching || isDebouncePending,
    enabled: shouldSearch || isDebouncePending,
  });

  return {
    destinationStateOrDepartmentOptions: options,
    isDestinationStateOrDepartmentsLoading: shouldSearch && isFetching,
    destinationStateOrDepartmentsError: error,
  };
};

export default useDestinationStateOrDepartmentsAutocomplete;
